"use client";

import { useState } from 'react';
import { useCertificationTypes, CertificationCategory, CertificationType } from '@/hooks/useCertificationTypes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, GripVertical, ArrowRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

export default function AdminCertificationsPage() {
  const { categories, types, isLoading } = useCertificationTypes();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [selectedCategory, setSelectedCategory] = useState<CertificationCategory | null>(null);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CertificationCategory | null>(null);
  const [editingType, setEditingType] = useState<CertificationType | null>(null);
  
  const [categoryForm, setCategoryForm] = useState({ name: '', display_order: 0 });
  const [typeForm, setTypeForm] = useState({ name: '', description: '', display_order: 0 });

  const handleCreateCategory = async () => {
    try {
      const { error } = await supabase
        .from('certification_categories')
        .insert({
          name: categoryForm.name,
          display_order: categoryForm.display_order || categories.length + 1,
        });

      if (error) throw error;

      toast({ title: "Success", description: "Category created successfully" });
      setShowCategoryDialog(false);
      setCategoryForm({ name: '', display_order: 0 });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) return;

    try {
      const { error } = await supabase
        .from('certification_categories')
        .update({ name: categoryForm.name, display_order: categoryForm.display_order })
        .eq('id', editingCategory.id);

      if (error) throw error;

      toast({ title: "Success", description: "Category updated successfully" });
      setShowCategoryDialog(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', display_order: 0 });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleDeactivateCategory = async (id: string) => {
    if (!confirm('Are you sure you want to deactivate this category?')) return;

    try {
      const { error } = await supabase
        .from('certification_categories')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;

      toast({ title: "Success", description: "Category deactivated" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleCreateType = async () => {
    if (!selectedCategory) return;

    try {
      const { error } = await supabase
        .from('certification_types')
        .insert({
          category_id: selectedCategory.id,
          name: typeForm.name,
          description: typeForm.description || null,
          display_order: typeForm.display_order || types.filter(t => t.category_id === selectedCategory.id).length + 1,
        });

      if (error) throw error;

      toast({ title: "Success", description: "Type created successfully" });
      setShowTypeDialog(false);
      setTypeForm({ name: '', description: '', display_order: 0 });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleUpdateType = async () => {
    if (!editingType) return;

    try {
      const { error } = await supabase
        .from('certification_types')
        .update({
          name: typeForm.name,
          description: typeForm.description || null,
          display_order: typeForm.display_order,
        })
        .eq('id', editingType.id);

      if (error) throw error;

      toast({ title: "Success", description: "Type updated successfully" });
      setShowTypeDialog(false);
      setEditingType(null);
      setTypeForm({ name: '', description: '', display_order: 0 });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleDeactivateType = async (id: string) => {
    if (!confirm('Are you sure you want to deactivate this type?')) return;

    try {
      const { error } = await supabase
        .from('certification_types')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;

      toast({ title: "Success", description: "Type deactivated" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const openEditCategory = (category: CertificationCategory) => {
    setEditingCategory(category);
    setCategoryForm({ name: category.name, display_order: category.display_order });
    setShowCategoryDialog(true);
  };

  const openEditType = (type: CertificationType) => {
    setEditingType(type);
    setTypeForm({ name: type.name, description: type.description || '', display_order: type.display_order });
    setShowTypeDialog(true);
  };

  const categoryTypes = selectedCategory 
    ? types.filter(t => t.category_id === selectedCategory.id)
    : [];

  if (isLoading) {
    return <div className="container mx-auto p-6">Loading...</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Certification Management</h1>
          <p className="text-muted-foreground">Manage certification categories and types</p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/admin/verification')}
          className="flex items-center gap-2"
        >
          Review Vendor Certifications
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Categories Panel */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Categories</CardTitle>
                <CardDescription>{categories.length} categories</CardDescription>
              </div>
              <Button onClick={() => {
                setEditingCategory(null);
                setCategoryForm({ name: '', display_order: categories.length + 1 });
                setShowCategoryDialog(true);
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className={`p-3 border rounded-lg cursor-pointer hover:bg-accent transition-colors ${
                    selectedCategory?.id === category.id ? 'bg-accent border-primary' : ''
                  }`}
                  onClick={() => setSelectedCategory(category)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{category.name}</div>
                        <div className="text-xs text-muted-foreground">Order: {category.display_order}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditCategory(category);
                        }}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeactivateCategory(category.id);
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Types Panel */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Certification Types</CardTitle>
                <CardDescription>
                  {selectedCategory ? (
                    <>{categoryTypes.length} types in {selectedCategory.name}</>
                  ) : (
                    'Select a category to view types'
                  )}
                </CardDescription>
              </div>
              {selectedCategory && (
                <Button onClick={() => {
                  setEditingType(null);
                  setTypeForm({ name: '', description: '', display_order: categoryTypes.length + 1 });
                  setShowTypeDialog(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Type
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {selectedCategory ? (
              <div className="space-y-2">
                {categoryTypes.map((type) => (
                  <div key={type.id} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{type.name}</div>
                          {type.description && (
                            <div className="text-xs text-muted-foreground mt-1">{type.description}</div>
                          )}
                          <div className="text-xs text-muted-foreground">Order: {type.display_order}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditType(type)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeactivateType(type.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {categoryTypes.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No types in this category yet
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Select a category to manage its types
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Create Category'}</DialogTitle>
            <DialogDescription>
              {editingCategory ? 'Update the category details' : 'Add a new certification category'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="category-name">Category Name</Label>
              <Input
                id="category-name"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Quality Certifications"
              />
            </div>
            <div>
              <Label htmlFor="category-order">Display Order</Label>
              <Input
                id="category-order"
                type="number"
                value={categoryForm.display_order}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>
                Cancel
              </Button>
              <Button onClick={editingCategory ? handleUpdateCategory : handleCreateCategory}>
                {editingCategory ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Type Dialog */}
      <Dialog open={showTypeDialog} onOpenChange={setShowTypeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingType ? 'Edit Type' : 'Create Type'}</DialogTitle>
            <DialogDescription>
              {editingType ? 'Update the type details' : `Add a new type to ${selectedCategory?.name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="type-name">Type Name</Label>
              <Input
                id="type-name"
                value={typeForm.name}
                onChange={(e) => setTypeForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., ISO 9001 (Quality Management)"
              />
            </div>
            <div>
              <Label htmlFor="type-description">Description (Optional)</Label>
              <Textarea
                id="type-description"
                value={typeForm.description}
                onChange={(e) => setTypeForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Additional details about this certification type"
              />
            </div>
            <div>
              <Label htmlFor="type-order">Display Order</Label>
              <Input
                id="type-order"
                type="number"
                value={typeForm.display_order}
                onChange={(e) => setTypeForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowTypeDialog(false)}>
                Cancel
              </Button>
              <Button onClick={editingType ? handleUpdateType : handleCreateType}>
                {editingType ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
